import { useState } from "react";
import { useTranslation } from "react-i18next";
import "../../../../i18n";
import { KeyValues, Notice, Pipeline, ValidationRule, XmlView } from "../../../../design/components";
import { ApiError } from "../../../../api/client";
import { useEntity } from "../../../../api/queries";
import type { TestDocumentResult } from "../../../../api/types";
import { familyLabel, familyShort, typeCodeLabel, type Family } from "../../../../domain/families";
import { enabledFamiliesOf } from "../../mapping/profileState";
import { runTestDocument } from "../onboardApi";
import { qrInformation } from "../qrInformation";
import { testDocumentKey, useOnboardDraft } from "../useOnboardDraft";
import styles from "../onboard.module.css";

const STAGES = ["Mapped", "Built", "Validated"] as const;
const STAGE_INDEX: Record<string, number> = { mapped: 0, built: 1, validated: 2 };

const NO_SAMPLE =
  "No document has reached the hub from this company yet. Paste one below to run the test.";

/** A stage that fails with something other than Schematron carries no id of its own. */
function stageLabel(stage: string): string {
  const at = STAGE_INDEX[stage];
  return at === undefined ? stage : STAGES[at]!;
}

/** Only SCHEMATRON_FATAL prefixes an id, and joins several failures with "; ". */
export function splitIssues(message: string, stage: string): { code: string; text: string }[] {
  return message.split("; ").map((part) => {
    const at = part.indexOf(": ");
    const code = at > 0 ? part.slice(0, at) : "";
    // A rule or field id never has a space in it; anything else is the message itself.
    if (!code || /\s/.test(code)) return { code: stageLabel(stage), text: part };
    return { code, text: part.slice(at + 2) };
  });
}

/** `Built a self-billed invoice (389) for Supplier self-billed invoices.` / `Built an invoice (380) for …` */
export function builtLine(result: Pick<TestDocumentResult, "family" | "typeCode">): string | null {
  if (!result.typeCode) return null;
  const type = typeCodeLabel(result.typeCode).toLowerCase();
  const article = /^[aeiou]/.test(type) ? "an" : "a";
  return `Built ${article} ${type} (${result.typeCode}) for ${familyLabel(result.family)}.`;
}

/** Step 5: one dry run per document family the company sends (HUB-F13). */
export function StepTestDocument({
  entityId,
  onContinue,
  onPassed,
}: {
  entityId: string;
  onContinue(): void;
  onPassed(family: Family, passed: boolean): void;
}) {
  const { t } = useTranslation();
  const entity = useEntity(entityId);
  const families = enabledFamiliesOf(entity.data?.enabledDocTypes);
  // A green run survives leaving the step; the wizard keeps it in the draft store.
  const remembered = useOnboardDraft((draft) => draft.testDocument);
  const [chosen, setChosen] = useState<Family | null>(null);
  const [passes, setPasses] = useState<Partial<Record<Family, boolean>>>({});
  const [result, setResult] = useState<TestDocumentResult | null>(null);
  const [needsSample, setNeedsSample] = useState(false);
  const [sampleText, setSampleText] = useState("");
  const [error, setError] = useState("");
  const [running, setRunning] = useState(false);

  const family: Family = chosen && families.includes(chosen) ? chosen : families[0]!;
  const passedFor = (item: Family): boolean =>
    passes[item] ?? remembered[testDocumentKey(entityId, item)] === true;

  function record(item: Family, passed: boolean): void {
    setPasses((prev) => ({ ...prev, [item]: passed }));
    onPassed(item, passed);
  }

  async function run(): Promise<void> {
    setRunning(true);
    setError("");
    let sample: unknown;
    if (sampleText.trim()) {
      try {
        sample = JSON.parse(sampleText);
      } catch {
        setError(t("onboard.testdoc.invalidJson", "That sample is not valid JSON."));
        setRunning(false);
        return;
      }
    }
    try {
      const outcome = await runTestDocument(entityId, family, sample);
      setResult(outcome);
      setNeedsSample(false);
      record(family, outcome.ok && !outcome.issues.some((issue) => issue.severity === "error"));
    } catch (exc) {
      setResult(null);
      record(family, false);
      if (exc instanceof ApiError && exc.code === "NO_SAMPLE") setNeedsSample(true);
      else
        setError(
          exc instanceof ApiError ? exc.message : t("onboard.testdoc.runError", "The test document could not be run."),
        );
    } finally {
      setRunning(false);
    }
  }

  const known = result ? STAGE_INDEX[result.stage] : undefined;
  const at = known ?? 0;
  const fatal = result?.issues.filter((issue) => issue.severity === "error") ?? [];
  const stillToRun = families.filter((item) => !passedFor(item));
  const ready = stillToRun.length === 0 && fatal.length === 0;
  const qr = result?.xml ? qrInformation(result.xml) : [];
  const fatalCodes = result
    ? fatal.flatMap((issue) => splitIssues(issue.message, result.stage)).map((issue) => issue.code)
    : [];
  const built = result ? builtLine(result) : null;

  return (
    <section className={styles.panel}>
      <h2>{t("onboard.testdoc.title", "Test document")}</h2>
      <p className={styles.caption}>
        {t(
          "onboard.testdoc.caption",
          "One document is mapped, built and checked against the rules, and then thrown away. Nothing is stored and nothing is sent onward. Run one sample for each family this company sends.",
        )}
      </p>

      {families.length > 1 ? (
        <div role="group" aria-label={t("onboard.testdoc.familyGroupLabel", "Document family")} className={styles.chips}>
          {families.map((item) => (
            <button
              key={item}
              type="button"
              className="btn btn-sm"
              aria-pressed={item === family}
              disabled={running}
              onClick={() => setChosen(item)}
            >
              {familyShort(item)}
            </button>
          ))}
        </div>
      ) : null}

      <button type="button" className="btn btn-primary" disabled={running} onClick={() => void run()}>
        {t("onboard.testdoc.runButton", "Run test document")}
      </button>

      {needsSample ? (
        <>
          <Notice kind="info">{t("onboard.testdoc.noSample", NO_SAMPLE)}</Notice>
          <div className={styles.field}>
            <label htmlFor="testdoc-sample">{t("onboard.testdoc.sampleLabel", "Sample document (JSON)")}</label>
            <textarea
              id="testdoc-sample"
              className="input"
              rows={6}
              value={sampleText}
              onChange={(event) => setSampleText(event.target.value)}
            />
          </div>
        </>
      ) : null}

      {error ? <Notice kind="fail">{error}</Notice> : null}

      {result ? (
        <>
          <div role="group" aria-label={t("onboard.testdoc.stagesLabel", "Test document stages")} className={styles.probe}>
            {/* An unrecognised stage marks nothing failed rather than blaming Mapped. */}
            <Pipeline
              current={at}
              stages={STAGES}
              {...(result.ok || known === undefined ? {} : { failedAt: known })}
            />
          </div>

          {built ? <p className={styles.discovered}>{built}</p> : null}

          <div role="group" aria-label={t("onboard.testdoc.validationResultLabel", "Validation result")} className={styles.probe}>
            <h3>{t("onboard.testdoc.validationResult", "Validation result")}</h3>
            {result.issues.length === 0 ? (
              <Notice kind="ok">{t("onboard.testdoc.noRuleFailed", "No rule failed on this document.")}</Notice>
            ) : (
              result.issues.flatMap((issue, index) =>
                splitIssues(issue.message, result.stage).map(({ code, text }, part) => (
                  <ValidationRule
                    key={`${index}-${part}`}
                    code={code}
                    title={text}
                    state={issue.severity === "error" ? "fail" : "warn"}
                  />
                )),
              )
            )}
          </div>

          <div role="group" aria-label={t("onboard.testdoc.qrInfoLabel", "QR information")} className={styles.probe}>
            <h3>{t("onboard.testdoc.qrInfo", "QR information")}</h3>
            <p className={styles.caption}>
              {t(
                "onboard.testdoc.qrInfoCaption",
                "The values this receipt QR carries. The image itself is produced when the document is submitted.",
              )}
            </p>
            {qr.length > 0 ? (
              <KeyValues
                pairs={qr.map((entry) => [
                  entry.label,
                  entry.value ?? <span className={styles.empty}>{t("onboard.testdoc.notPresent", "Not present")}</span>,
                ])}
              />
            ) : (
              <p className={styles.empty}>{t("onboard.testdoc.noQrValues", "This document carries no QR values.")}</p>
            )}
          </div>

          {result.xml ? (
            <div className={styles.probe}>
              <h3>{t("onboard.testdoc.renderedXml", "Rendered XML")}</h3>
              <XmlView src={result.xml} />
            </div>
          ) : null}
        </>
      ) : null}

      {fatal.length > 0 ? (
        <Notice kind="fail">
          {t("onboard.testdoc.fixMapping", "Fix the mapping in the Studio and run the test again: {{codes}}.", {
            codes: fatalCodes.join(", "),
          })}
        </Notice>
      ) : null}

      {families.length > 1 && fatal.length === 0 && stillToRun.length > 0 ? (
        <p className={styles.empty}>
          {t("onboard.testdoc.stillToRun", "Still to run: {{families}}.", {
            families: stillToRun.map(familyShort).join(", "),
          })}
        </p>
      ) : null}

      <div className={styles.foot}>
        <button type="button" className="btn btn-primary" disabled={!ready} onClick={onContinue}>
          {t("onboard.testdoc.continue", "Continue")}
        </button>
      </div>
    </section>
  );
}
